import { CanonicalConditional } from '@/composables/useValidationItem'
import COUNTRY_ISO from 'iso-3166-1'
import ISO639_1 from 'iso-639-1'
import i18n from '@/plugins/i18n'
import { IS_LANXESS } from '@/config/customers'

interface PropItemValidation {
  identifier: string
  maxLength: number
  maxDecimals: number
  decimalSymbol: string
  canBeNegative: boolean
  splitSymbol?: string
  validFormatExample?: string
  canContainThousandsSeparator?: boolean
}

const SEC_KEY_REGEX = /SEC_[A-Z0-9_.]+/g

const DORIS_CANONICALS_VALIDATIONS: CanonicalConditional[] = [
  {
    canonicalField: 'SEC_0_1.REV_DATE',
    matchesRule: 'all',
    matches: [
      { type: 'item', testType: 'exists' },
      {
        type: 'itemStatus',
        testType: 'is',
        valueToTest: 'verified',
      },
      {
        type: 'string',
        testType: '!=',
        valueToTest: '',
      },
    ],
    errorMessage: 'Must exist, not be empty and be verified',
  },
  {
    canonicalField: 'SEC_0_1.ACCESS_LEVEL',
    matchesRule: 'all',
    matches: [
      { type: 'item', testType: 'exists' },
      {
        type: 'itemStatus',
        testType: 'is',
        valueToTest: 'verified',
      },
      {
        type: 'string',
        testType: '!=',
        valueToTest: '',
      },
    ],
    errorMessage: 'Must exist, not be empty and be verified',
  },
  {
    canonicalField: 'SEC_0_1.TYPE',
    matchesRule: 'all',
    matches: [
      { type: 'item', testType: 'exists' },
      {
        type: 'itemStatus',
        testType: 'is',
        valueToTest: 'verified',
      },
      {
        type: 'string',
        testType: '!=',
        valueToTest: '',
      },
    ],
    errorMessage: 'Must exist, not be empty and be verified',
  },
  {
    canonicalField: 'SEC_0_1.REV_DATE',
    matchesRule: 'all',
    matches: [
      {
        type: 'currentDate',
        testType: 'validFormat',
        format: 'YYYY-MM-DD',
      },
    ],
    errorMessage: 'Invalid date format',
  },
  {
    canonicalField: 'SEC_0_1.PRINT_DATE',
    skipIfEmpty: true,
    matchesRule: 'all',
    matches: [
      {
        type: 'currentDate',
        testType: 'validFormat',
        format: 'YYYY-MM-DD',
      },
    ],
    errorMessage: 'Invalid date format',
  },
  {
    canonicalField: 'SEC_0_1.RECEIVE_DATE',
    skipIfEmpty: true,
    matchesRule: 'all',
    matches: [
      {
        type: 'currentDate',
        testType: 'validFormat',
        format: 'YYYY-MM-DD',
      },
    ],
    errorMessage: 'Invalid date format',
  },
  {
    canonicalField: 'SEC_0_1.REV_DATE',
    skipIfEmpty: true,
    matchesRule: 'all',
    matches: [
      {
        type: 'currentDate',
        testType: '<=',
      },
    ],
    errorMessage: 'Must be less than or equal to current date',
  },
  {
    canonicalField: 'SEC_0_1.PRINT_DATE',
    skipIfEmpty: true,
    matchesRule: 'all',
    matches: [
      {
        type: 'canonical',
        testType: '>=',
        canonicalToTest: 'SEC_0_1.REV_DATE',
        valueType: 'date',
      },
      {
        type: 'currentDate',
        testType: '<=',
      },
    ],
    errorMessage: 'Must be smaller equals to current date and higher equals to SEC_0_1.REV_DATE',
  },
  {
    canonicalField: 'SEC_0_1.RECEIVE_DATE',
    skipIfEmpty: true,
    matchesRule: 'all',
    matches: [
      {
        type: 'canonical',
        testType: '>=',
        canonicalToTest: 'SEC_0_1.REV_DATE',
        valueType: 'date',
      },
      {
        type: 'currentDate',
        testType: '<=',
      },
    ],
    errorMessage: 'Must be smaller equals to current date and higher equals to SEC_0_1.REV_DATE',
  },
  {
    canonicalField: 'SEC_0_1.VALIDITY_DATE_CONFIRMATION',
    skipIfEmpty: true,
    matchesRule: 'all',
    matches: [
      {
        type: 'canonical',
        testType: '>=',
        canonicalToTest: 'SEC_0_1.REV_DATE',
        valueType: 'date',
      },
      {
        type: 'currentDate',
        testType: '<=',
      },
    ],
    errorMessage: 'Must be smaller equals to current date and higher equals to SEC_0_1.REV_DATE',
  },
  {
    canonicalField: 'SEC_0_1.UA_UN_NUMBER',
    matchesRule: 'all',
    matches: [
      {
        type: 'number',
        testType: '<=',
        valueToTest: 7,
      },
    ],
    errorMessage: 'Must be less than or equal to 7',
  },
  {
    canonicalField: 'SEC_0_1.FORMAT',
    matchesRule: 'all',
    matches: [
      {
        type: 'array',
        testType: 'includes',
        valueToTest: ['EU', 'US', 'AP', 'MX', 'CA', 'GHS', 'GB', 'Other'],
      },
    ],
    errorMessage: 'It has an invalid value',
  },
  {
    canonicalField: 'SEC_0_1.COUNTRY',
    matchesRule: 'all',
    matches: [
      {
        type: 'array',
        testType: 'includes',
        valueToTest: ['GLO', ...COUNTRY_ISO.all().map((c) => c.alpha2.toUpperCase())],
      },
    ],
    errorMessage: 'It has an unsupported country code value',
  },
  {
    canonicalField: 'SEC_0_1.LANGUAGE',
    matchesRule: 'all',
    matches: [
      {
        type: 'array',
        testType: 'includes',
        valueToTest: ISO639_1.getAllCodes().map((i) => i.toUpperCase()),
      },
    ],
    errorMessage: 'It has an unsupported language code value',
  },
  {
    canonicalField: 'SEC_0_1.DISTRIBUTION_TO_HSA',
    matchesRule: 'any',
    skipIfEmpty: true,
    matches: [
      {
        type: 'string',
        testType: '==',
        valueToTest: 'YES',
      },
      {
        type: 'string',
        testType: '==',
        valueToTest: 'NO',
      },
    ],
    errorMessage: `Only the values 'YES' or 'NO' are allowed`,
  },
]

// In the future, the list will come from the backend, so this function will take the text and attempt to translate the items that are canonical
export function translateSECKeys(message: string): string {
  // eslint-disable-next-line
  // @ts-ignore
  return message.replace(SEC_KEY_REGEX, (key) => (hasTranslation(key) ? i18n.global.t(key) : key))
}

function hasTranslation(key: string): boolean {
  return i18n.global.t(key) !== key
}

function getPropItemsValidations(): PropItemValidation[] {
  const propItemsValidations: PropItemValidation[] = []

  if (IS_LANXESS) {
    propItemsValidations.push(
      ...[
        {
          identifier: 'SAP_EHS_1013_019_VALUE',
          maxLength: 4,
          maxDecimals: 2,
          decimalSymbol: '.',
          canBeNegative: true,
          splitSymbol: ' - ',
          validFormatExample: '-5.2 - 10',
        },
        {
          identifier: 'SAP_EHS_1013_019_EC_TEMP',
          maxLength: 7,
          maxDecimals: 2,
          decimalSymbol: '.',
          canBeNegative: true,
          canContainThousandsSeparator: true,
        },
      ]
    )
  }

  return propItemsValidations
}

function getCanonicalsValidations(): CanonicalConditional[] {
  const canonicalsValidations: CanonicalConditional[] = []

  if (window.ENV.ALLOW_SEND_DATA_TO_DORIS) {
    canonicalsValidations.push(...DORIS_CANONICALS_VALIDATIONS)
  }

  return canonicalsValidations
}

export const VALIDATION_LIST: {
  propItemsValidations: PropItemValidation[]
  canonicalsValidations: CanonicalConditional[]
} = {
  propItemsValidations: getPropItemsValidations(),
  canonicalsValidations: getCanonicalsValidations(),
}
